import type { ReactNode } from 'react';
import { App, Alert, Button, Card, Col, Popconfirm, Progress, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, CreditCardOutlined, DeleteOutlined, DisconnectOutlined, InboxOutlined, QuestionCircleOutlined, ReloadOutlined, SafetyCertificateOutlined, StopOutlined } from '@ant-design/icons';
import { CardTitle, StatCard } from '../../components/StatCard';
import { useEffect, useRef, useState } from 'react';
import type { VerificationJobItem, VerifyVendor } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { STATUS_LABEL, useStopVerificationJob, useVerificationJobs, useVerificationStatus, useVerifySelection } from '../../features/verification/api';
import { LoadError, tableLocale, tableLoading } from '../../components/states';
import { fmtDateTime, plural } from '../../utils/format';
import { MarkInvalidModal } from './MarkInvalidModal';
import { ReverifyModal } from './ReverifyModal';
import { RemoveBadEmailsModal } from './RemoveBadEmailsModal';
import { CampaignSendSettings } from './CampaignSendSettings';
import { fmtDuration, isActiveJob, jobTiming, VerificationProgress } from './VerificationProgress';
import { useFeatures } from '../../features/settings/api';

const JOB_COLOR: Record<string, string> = { PENDING: 'default', RUNNING: 'processing', STOPPED: 'warning', COMPLETED: 'success', FAILED: 'error' };
const JOB_LABEL: Record<string, string> = { PENDING: 'Queued', RUNNING: 'Running', STOPPED: 'Stopped', COMPLETED: 'Completed', FAILED: 'Failed' };
const VENDOR_LABEL: Record<string, string> = { auto: 'Auto', builtin: 'Built-in', zerobounce: 'ZeroBounce', emailable: 'Emailable', myemailverifier: 'MyEmailVerifier' };
/** A finished job stays in the progress panel this long so the result is visible after it ends. */
const RECENT_MS = 15 * 60_000;
const STATUS_META: Record<string, { icon: ReactNode; note: string }> = {
  VALID: { icon: <CheckCircleOutlined />, note: 'Mailbox confirmed or checks passed' },
  UNKNOWN: { icon: <QuestionCircleOutlined />, note: 'Checks passed, mailbox not confirmed' },
  UNVERIFIED: { icon: <ClockCircleOutlined />, note: 'Not checked yet' },
  CATCH_ALL: { icon: <InboxOutlined />, note: 'Domain accepts any address' },
  INVALID: { icon: <CloseCircleOutlined />, note: 'Will bounce; skipped by campaigns' },
  DISPOSABLE: { icon: <DeleteOutlined />, note: 'Throwaway mailbox provider' },
  SUPPRESSED: { icon: <StopOutlined />, note: 'Bounced, complained or unsubscribed' },
};

export function VerificationPage() {
  const { message } = App.useApp();
  const campaignsOn = useFeatures().enabled('campaigns');
  const status = useVerificationStatus();
  const jobs = useVerificationJobs();
  const verify = useVerifySelection();
  const stop = useStopVerificationJob();
  const [vendor, setVendor] = useState<VerifyVendor>('auto');
  const [removerOpen, setRemoverOpen] = useState(false);
  const [reverifyOpen, setReverifyOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const counts = status.data?.counts ?? {};
  const badCount = (counts.INVALID ?? 0) + (counts.DISPOSABLE ?? 0) + (counts.SUPPRESSED ?? 0);
  const active = (jobs.data ?? []).filter(isActiveJob);
  const latest = jobs.data?.[0];
  const recent = latest?.finishedAt && Date.now() - Date.parse(latest.finishedAt) < RECENT_MS ? latest : undefined;
  const shown = active.length ? active : recent ? [recent] : [];

  // refresh the status counters when a job finishes (not on every poll: status also asks the vendor for credits)
  const activeCount = useRef(active.length);
  useEffect(() => {
    if (active.length < activeCount.current) void status.refetch();
    activeCount.current = active.length;
  }, [active.length, status]);
  const stopJob = (id: string) => stop.mutateAsync(id).then(() => message.success('Verification stopped')).catch((e) => message.error(errorMessage(e)));
  const autoVendor = status.data?.autoVendor ?? null;
  // matches what a job will pick up: already-verified contacts are skipped
  const vendorWillRun = vendor === 'auto' ? !!autoVendor : vendor !== 'builtin';
  const unverified = (counts.UNVERIFIED ?? 0) + (vendorWillRun ? (status.data?.awaitingVendor ?? 0) : 0);

  return (
    <div className="page">
      <PageHeader
        title="Verification"
        subtitle="Built-in checks run on every new address (syntax, disposable domains, role mailboxes, mail server). A vendor check confirms the mailbox exists."
        extra={
          <Space wrap>
            <Button icon={<DisconnectOutlined />} onClick={() => setRemoverOpen(true)}>
              Mass email remover
            </Button>
            <Button danger icon={<DeleteOutlined />} disabled={!badCount} onClick={() => setRemoveOpen(true)}>
              Remove bad emails ({badCount.toLocaleString()})
            </Button>
            <Select value={vendor} onChange={setVendor} style={{ width: 200 }} options={[{ value: 'auto', label: 'Built-in + vendor if available' }, { value: 'builtin', label: 'Built-in only (free)' }, { value: 'emailable', label: 'Emailable', disabled: !status.data?.emailable.configured }, { value: 'myemailverifier', label: 'MyEmailVerifier', disabled: !status.data?.myemailverifier.configured }, { value: 'zerobounce', label: 'ZeroBounce', disabled: !status.data?.zerobounce.configured }]} />
            <Button type="primary" icon={<SafetyCertificateOutlined />} loading={verify.isPending} disabled={!unverified || !status.data} onClick={() => verify.mutateAsync({ selection: { all: {} }, vendor, onlyUnverified: true }).then((j) => message.success(`Queued ${plural(j.total, 'contact')} for verification`)).catch((e) => message.error(errorMessage(e)))}>
              Verify all unverified ({unverified})
            </Button>
            <Button icon={<ReloadOutlined />} disabled={!status.data} onClick={() => setReverifyOpen(true)}>
              Re-verify…
            </Button>
          </Space>
        }
      />
      {status.isError && <div style={{ marginBottom: 16 }}><LoadError error={status.error} onRetry={status.refetch} title="Could not load verification status" /></div>}
      {status.data && !status.data.autoVendor && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title="No vendor configured" description="Set EMAILABLE_API_KEY, MYEMAILVERIFIER_API_KEY or ZEROBOUNCE_API_KEY on the API and worker to confirm mailboxes exist. Until then only the free built-in checks run, and addresses that pass stay Unknown." />
      )}
      {shown.map((j) => (
        <VerificationProgress key={j.id} job={j} onStop={() => stopJob(j.id)} stopping={stop.isPending && stop.variables === j.id} />
      ))}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        {(['VALID', 'UNKNOWN', 'UNVERIFIED', 'CATCH_ALL', 'INVALID', 'DISPOSABLE', 'SUPPRESSED'] as const).map((s) => (
          <Col xs={24} sm={12} xl={6} key={s}>
            <StatCard icon={STATUS_META[s].icon} label={STATUS_LABEL[s]} value={status.data ? (counts[s] ?? 0).toLocaleString() : '—'} note={STATUS_META[s].note} loading={status.isLoading} />
          </Col>
        ))}
        <Col xs={24} sm={12} xl={6}>
          <StatCard
            icon={<CreditCardOutlined />}
            label={autoVendor ? `${VENDOR_LABEL[autoVendor]} credits` : 'Vendor credits'}
            value={(autoVendor && status.data?.[autoVendor].credits?.toLocaleString()) || '—'}
            note={autoVendor ? 'Remaining vendor checks' : 'No vendor configured'}
            loading={status.isLoading}
          />
        </Col>
      </Row>
      {campaignsOn && <CampaignSendSettings counts={counts} />}
      <Card title={<CardTitle title="Verification jobs" subtitle="Bulk checks queued from here, from contact selections, or before a campaign sends" />}>
        <Table<VerificationJobItem>
          rowKey="id"
          loading={tableLoading(jobs)}
          locale={tableLocale(jobs, 'No verification jobs yet. Verify from here, from a contact selection, or before a campaign sends.')}
          dataSource={jobs.data ?? []}
          pagination={false}
          columns={[
            { title: 'Started', dataIndex: 'createdAt', width: 160, render: (v) => fmtDateTime(v) },
            { title: 'Scope', key: 'scope', render: (_, j) => j.scope.label ?? (j.scope.campaignId ? 'Campaign audience' : `${plural(j.total, 'contact')} to check`) },
            { title: 'Vendor', dataIndex: 'vendor', width: 150, render: (v: string, j) => (v === 'auto' && j.resolvedVendor ? `Auto → ${VENDOR_LABEL[j.resolvedVendor] ?? j.resolvedVendor}` : VENDOR_LABEL[v] ?? v) },
            { title: 'Status', dataIndex: 'status', width: 110, render: (s: string, j) => <Tag color={JOB_COLOR[s]} title={j.error ?? undefined}>{JOB_LABEL[s] ?? s}</Tag> },
            { title: 'Progress', key: 'progress', width: 200, render: (_, j) => <Progress size="small" status={j.status === 'STOPPED' ? 'normal' : undefined} percent={j.total ? Math.round((j.processed / j.total) * 100) : j.status === 'COMPLETED' ? 100 : 0} /> },
            { title: 'Duration', key: 'duration', width: 100, render: (_, j) => { const { elapsed } = jobTiming(j, Date.now()); return elapsed !== null ? fmtDuration(elapsed) : '—'; } },
            { title: 'Valid', dataIndex: 'validCount', width: 80 },
            { title: 'Invalid', dataIndex: 'invalidCount', width: 80 },
            { title: 'Unknown', dataIndex: 'unknownCount', width: 90 },
            { title: 'By', key: 'by', width: 140, render: (_, j) => j.createdBy?.name ?? <Typography.Text type="secondary">System</Typography.Text> },
            {
              key: 'actions',
              width: 90,
              render: (_, j) =>
                (j.status === 'PENDING' || j.status === 'RUNNING') && (
                  <Popconfirm title="Stop this verification?" description="Contacts checked so far keep their result." okText="Stop" onConfirm={() => stopJob(j.id)}>
                    <Button size="small" danger loading={stop.isPending && stop.variables === j.id}>Stop</Button>
                  </Popconfirm>
                ),
            },
          ]}
        />
      </Card>
      <MarkInvalidModal open={removerOpen} onClose={() => setRemoverOpen(false)} />
      <RemoveBadEmailsModal open={removeOpen} onClose={() => setRemoveOpen(false)} counts={counts} />
      <ReverifyModal open={reverifyOpen} onClose={() => setReverifyOpen(false)} status={status.data} vendor={vendor} vendorLabel={vendor === 'auto' ? (autoVendor ? `Auto (${VENDOR_LABEL[autoVendor]})` : 'Auto (built-in only)') : VENDOR_LABEL[vendor] ?? vendor} />
    </div>
  );
}
